import React, { useCallback, useEffect, useState } from "react";

import { resendMfaCode, verifyMfaCode } from "services/auth/api";
import { getApiErrorByCode } from "services/api/transformation";

import { TextField } from "ui/components/TextField/TextField";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";

import { MfaCodeFormProps } from "./types";

/** Kod ima točno šest znamenki; sve drugo je greška unosa, ne poziv na poslužitelj. */
const CODE_LENGTH = 6;
const CODE_PATTERN = /^\d{6}$/;

/** Greške nakon kojih izazov više ne postoji, pa jedino preostaje nova prijava. */
const DEAD_CHALLENGE_CODES = ["MfaChallengeNotFound", "MfaTooManyAttempts"];

/** Sekunde do trenutka kad poslužitelj dopušta novo slanje. */
const secondsUntil = (isoDate: string): number =>
  Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / 1000));

/**
 * Drugi korak prijave: unos koda koji je stigao e-poštom.
 *
 * Poruke o greškama s poslužitelja idu kroz snackbar kao i drugdje; ovdje se uz polje pokaže
 * samo ona koja se tiče samog koda (krivi kod, preostali pokušaji).
 */
const MfaCodeForm: React.FC<MfaCodeFormProps> = (props) => {
  const { challenge, onVerified, onChallengeRenewed, onBack } = props;

  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resendIn, setResendIn] = useState<number>(() =>
    secondsUntil(challenge.resendAvailableAt)
  );

  // Odbrojavanje do ponovnog slanja; kreće iznova sa svakim novim izazovom.
  useEffect(() => {
    setResendIn(secondsUntil(challenge.resendAvailableAt));

    const timer = window.setInterval(() => {
      setResendIn(secondsUntil(challenge.resendAvailableAt));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [challenge.resendAvailableAt]);

  const handleCodeChange = useCallback((e: any) => {
    // Iz e-pošte se kod često zalijepi s razmacima; čuvaju se samo znamenke.
    const digits = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    setError(undefined);
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!CODE_PATTERN.test(code)) {
      setError(`Upišite ${CODE_LENGTH}-znamenkasti kod iz e-pošte`);
      return;
    }

    setIsLoading(true);

    try {
      const tokens = await verifyMfaCode(challenge.challengeId, code);
      onVerified(tokens);
      return;
    } catch (err: any) {
      const invalidCode = getApiErrorByCode(err, "MfaCodeInvalid");

      if (invalidCode) {
        setError(invalidCode);
        setCode("");
      } else if (DEAD_CHALLENGE_CODES.some((c) => getApiErrorByCode(err, c))) {
        onBack();
        return;
      }
    }

    setIsLoading(false);
  };

  const handleResend = async () => {
    if (resendIn > 0 || isLoading) return;

    setIsLoading(true);

    try {
      onChallengeRenewed(await resendMfaCode(challenge.challengeId));
      setCode("");
      setError(undefined);
    } catch (err: any) {
      if (getApiErrorByCode(err, "MfaChallengeNotFound")) {
        onBack();
        return;
      }
    }

    setIsLoading(false);
  };

  return (
    <>
      <div className="text-center mb-4 text-[#344054] mb-3">
        <h1 className="text-2xl mb-1">Potvrda prijave</h1>
        <p className="text-base">
          Poslali smo kod za prijavu na <span className="font-medium">{challenge.email}</span>.
        </p>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-base mb-2 font-medium leading-6 text-[#344054]">
            Kod iz e-pošte
          </label>
          {/* Bez debounce-a: kod se najčešće zalijepi i odmah potvrdi, pa odgođena vrijednost
              ne bi stigla u stanje prije klika. */}
          <TextField
            type="text"
            name="code"
            autoComplete="one-time-code"
            placeholder="123456"
            onChange={handleCodeChange}
            error={error}
            value={code}
            width="full"
            height="big"
            maxLength={CODE_LENGTH}
            disableDebounce
            required
          />
        </div>
        <div className="mb-4">
          {/* Samo `submit`: klik i Enter idu kroz `onSubmit` forme, pa se kod ne šalje dvaput. */}
          <Button
            type="submit"
            variant="primary"
            size="full"
            disabled={isLoading}
            className="font-medium py-4 !text-base rounded-md"
          >
            Potvrdi
          </Button>
        </div>
      </form>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="font-medium text-sm text-slate-500 hover:text-slate-700"
        >
          Natrag na prijavu
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendIn > 0 || isLoading}
          className="font-medium text-sm text-blue-500 hover:text-blue-400 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          {resendIn > 0 ? `Pošalji novi kod (${resendIn}s)` : "Pošalji novi kod"}
        </button>
      </div>
      <Loader isLoading={isLoading}> </Loader>
    </>
  );
};

export default MfaCodeForm;
